import {
  AlertCircle,
  ChevronRight,
  Target,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { DashboardApprovalChart } from "@/components/dashboard/charts/approval"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { DashboardPaymentTypeEnum } from "@/enums"
import { useDashboardPayment } from "@/hooks"

interface ApprovalData {
  general: number
  firstInstallment: number
  otherInstallments: number
}

interface RefusalReason {
  key: string
  value: string
}

interface OperationalPerformanceProps {
  approvalData: ApprovalData | null
  refusalReasons: RefusalReason[]
  approvalIndexGeneralType: DashboardPaymentTypeEnum
  searchParams: SearchParamsType
  enabled?: boolean
}

export const OperationalPerformance: React.FC<
  OperationalPerformanceProps
> = ({
  approvalData,
  refusalReasons,
  approvalIndexGeneralType,
  searchParams,
  enabled = true,
}) => {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    data: approvalIndexGeneralRecurring,
    isFetching: isLoadingApprovalIndex,
  } = useDashboardPayment({
    ...searchParams,
    dashboardType: approvalIndexGeneralType,
    enabled,
  })
  const approvalGeneralValue =
    approvalIndexGeneralRecurring?.indicator
      ?.find((item) => item.key === "Aprovado")
      ?.value.replace("%", "")
      ? Number(
          approvalIndexGeneralRecurring.indicator
            .find((item) => item.key === "Aprovado")
            ?.value.replace("%", "") || "0"
        )
      : 0
  const top5Reasons = refusalReasons?.slice(0, 5) || []
  const totalRefusals = refusalReasons?.reduce(
    (sum, r) => sum + Number(r.value),
    0
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <Target className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">
            {t(
              "pages.dashboard.sections.operationalPerformance.title",
              "Performance Operacional"
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "pages.dashboard.sections.operationalPerformance.description",
              "Aprovação e qualidade das transações"
            )}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Approval Index Card */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.approvalIndex.title"
                )}
              </CardTitle>
              <Target className="h-5 w-5 text-green-500" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {isLoadingApprovalIndex ? (
              <div className="text-sm text-muted-foreground">
                {t("common.loading", "Carregando...")}
              </div>
            ) : !approvalData ||
              approvalGeneralValue === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t("pages.dashboard.noDataAvailable")}
              </div>
            ) : (
              <>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Geral
                    </span>
                    <span className="font-medium">
                      {approvalGeneralValue.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      1ª Parcela
                    </span>
                    <span className="font-medium">
                      {approvalData?.firstInstallment.toFixed(
                        1
                      )}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Demais Parcelas
                    </span>
                    <span className="font-medium">
                      {approvalData?.otherInstallments.toFixed(
                        1
                      )}
                      %
                    </span>
                  </div>
                </div>
                <div className="pt-2">
                  <DashboardApprovalChart
                    type={approvalIndexGeneralType}
                    searchParams={searchParams}
                    enabled={enabled}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Top 5 Refusal Reasons */}
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.refusalReasons.title"
                )}{" "}
                - Top 5
              </CardTitle>
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {!refusalReasons ||
            refusalReasons.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t("pages.dashboard.noDataAvailable")}
              </div>
            ) : (
              top5Reasons.map((reason, index) => {
                const percentage =
                  totalRefusals > 0
                    ? (Number(reason.value) /
                        totalRefusals) *
                      100
                    : 0
                return (
                  <div
                    key={reason.key}
                    className="flex items-center justify-between rounded-md bg-red-50/50 px-3 py-2.5 text-sm transition-colors hover:bg-red-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-xs font-semibold text-red-700">
                        {index + 1}
                      </span>
                      <span
                        title={reason.key}
                        className="line-clamp-1 font-medium text-red-900"
                      >
                        {reason.key}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-600">
                        {percentage.toFixed(1)}%
                      </span>
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        {reason.value}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
            {refusalReasons &&
              refusalReasons.length > 5 && (
                <Dialog
                  open={isDialogOpen}
                  onOpenChange={setIsDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 w-full text-xs text-muted-foreground hover:text-primary"
                    >
                      Ver mais ({refusalReasons.length - 5}{" "}
                      motivos)
                      <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[85vh] max-w-[95vw] sm:max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        {t(
                          "pages.dashboard.cards.refusalReasons.title",
                          "Motivos de recusa"
                        )}
                      </DialogTitle>
                      <DialogDescription>
                        {t(
                          "pages.dashboard.cards.refusalReasons.allReasons",
                          `Total de ${refusalReasons.length} motivos de recusa`
                        )}
                      </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[60vh] pr-4">
                      <div className="space-y-2">
                        {refusalReasons.map(
                          (reason, index) => {
                            const percentage =
                              totalRefusals > 0
                                ? (Number(reason.value) /
                                    totalRefusals) *
                                  100
                                : 0
                            return (
                              <div
                                key={reason.key}
                                className="flex items-center justify-between rounded-md bg-red-50/50 px-3 py-2.5 text-sm transition-colors hover:bg-red-50"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-semibold text-red-700">
                                    {index + 1}
                                  </span>
                                  <span className="break-words font-medium text-red-900">
                                    {reason.key}
                                  </span>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                  <span className="hidden text-xs text-red-600 sm:inline">
                                    {percentage.toFixed(1)}%
                                  </span>
                                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                                    {reason.value}
                                  </span>
                                </div>
                              </div>
                            )
                          }
                        )}
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
